import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useColorModeValue,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Checkbox,
  Text,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { CustomPopup } from "@root/components/common/modals/CustomPopup";
import { AddIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useDepartmentContext } from "@root/context/department-contxt";
import { useEquipmentContext } from "@root/context/euqipment-context";
import { useMaterialContext } from "@root/context/material-context";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { useSelector } from "react-redux";
import { getCurrentLanguage } from "@root/store/selectors/features";

type SelectorType = "equipment" | "material";

interface CreateAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AssessmentFormData) => Promise<void> | void;
  onSubmitSuccess?: () => void;
}

const CreateAssessmentModal: React.FC<CreateAssessmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation("common");
  const {
    departmentData,
    fetchDepartments,
    departmentHasMore,
    getDepartmentsLoading,
  } = useDepartmentContext();
  const {
    equipmentsByDepartmentData,
    fetchEquipmentsByDepartment,
    equipmentHasMore,
    getEquipmentsByDepartmentLoading,
  } = useEquipmentContext();
  const {
    materialsByDepartmentData,
    fetchMaterialsByDepartment,
    materialHasMore,
    getMaterialsByDepartmentLoading,
  } = useMaterialContext();
  // Internal state for managing arrays - now storing IDs
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<number[]>(
    []
  );
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>([]);

  // Custom department state
  const [customDepartment, setCustomDepartment] = useState("");
  const [isDepartmentMenuOpen, setIsDepartmentMenuOpen] = useState(false);
  const departmentInputRef = useRef<HTMLInputElement>(null);
  const departmentMenuRef = useRef<HTMLDivElement>(null);

  // Custom equipment state
  const [customEquipments, setCustomEquipments] = useState<string[]>([]);
  const [equipmentInputValue, setEquipmentInputValue] = useState("");
  const [isEquipmentMenuOpen, setIsEquipmentMenuOpen] = useState(false);
  const equipmentMenuRef = useRef<HTMLDivElement>(null);

  // Custom material state
  const [customMaterials, setCustomMaterials] = useState<string[]>([]);
  const [materialInputValue, setMaterialInputValue] = useState("");
  const [isMaterialMenuOpen, setIsMaterialMenuOpen] = useState(false);
  const materialMenuRef = useRef<HTMLDivElement>(null);

  // Search states for dropdowns
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [materialSearch, setMaterialSearch] = useState("");

  // Pagination/offset states for infinite scroll
  const [departmentOffset, setDepartmentOffset] = useState(0);
  const [equipmentOffset, setEquipmentOffset] = useState(0);
  const [materialOffset, setMaterialOffset] = useState(0);

  const ITEMS_PER_PAGE = PAGINATION_DEFAULTS.PAGESIZE;
  const currentLanguageApp = useSelector(getCurrentLanguage);

  const [formData, setFormData] = useState<AssessmentFormData>({
    assessmentName: "",
    department: null,
    equipment: "",
    materials: "",
    workDescription: "",
  });

  // Initial fetch of departments
  useEffect(() => {
    fetchDepartments({
      searchQuery: "",
      offset: 0,
      limit: ITEMS_PER_PAGE,
      append: false,
    });
    setDepartmentOffset(ITEMS_PER_PAGE);
  }, []);

  useEffect(() => {
    fetchDepartments({
      searchQuery: "",
      offset: 0,
      limit: ITEMS_PER_PAGE,
      append: false,
    });
    setDepartmentOffset(ITEMS_PER_PAGE);
  }, [currentLanguageApp]);

  // Refetch departments when search changes (reset offset)
  useEffect(() => {
    fetchDepartments({
      searchQuery: departmentSearch,
      offset: 0,
      limit: ITEMS_PER_PAGE,
      append: false,
    });
    setDepartmentOffset(ITEMS_PER_PAGE);
  }, [departmentSearch]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        departmentMenuRef.current &&
        !departmentMenuRef.current.contains(event.target as Node)
      ) {
        setIsDepartmentMenuOpen(false);
      }
      if (
        equipmentMenuRef.current &&
        !equipmentMenuRef.current.contains(event.target as Node)
      ) {
        setIsEquipmentMenuOpen(false);
      }
      if (
        materialMenuRef.current &&
        !materialMenuRef.current.contains(event.target as Node)
      ) {
        setIsMaterialMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const resetDependentFields = () => {
    setSelectedEquipmentIds([]);
    setSelectedMaterialIds([]);
    setCustomEquipments([]);
    setCustomMaterials([]);
    setEquipmentInputValue("");
    setMaterialInputValue("");
    setEquipmentSearch("");
    setMaterialSearch("");
    setEquipmentOffset(0);
    setMaterialOffset(0);
  };

  // Fetch equipment and materials when department changes
  useEffect(() => {
    if (formData?.department?.id) {
      const departmentId = formData.department.id;
      fetchEquipmentsByDepartment({
        id: departmentId,
        searchQuery: "",
        offset: 0,
        limit: ITEMS_PER_PAGE,
        append: false,
      });
      fetchMaterialsByDepartment({
        id: departmentId,
        searchQuery: "",
        offset: 0,
        limit: ITEMS_PER_PAGE,
        append: false,
      });
      setEquipmentOffset(ITEMS_PER_PAGE);
      setMaterialOffset(ITEMS_PER_PAGE);
      resetDependentFields();
    }
  }, [formData?.department?.id]);

  // Refetch equipment when search changes (reset offset)
  useEffect(() => {
    if (formData?.department?.id) {
      fetchEquipmentsByDepartment({
        id: formData.department.id,
        searchQuery: equipmentSearch,
        offset: 0,
        limit: ITEMS_PER_PAGE,
        append: false,
      });
      setEquipmentOffset(ITEMS_PER_PAGE);
    }
  }, [equipmentSearch]);

  // Refetch materials when search changes (reset offset)
  useEffect(() => {
    if (formData?.department?.id) {
      fetchMaterialsByDepartment({
        id: formData.department.id,
        searchQuery: materialSearch,
        offset: 0,
        limit: ITEMS_PER_PAGE,
        append: false,
      });
      setMaterialOffset(ITEMS_PER_PAGE);
    }
  }, [materialSearch]);

  const [errors, setErrors] = useState<{
    assessmentName?: string;
    department?: string;
    workDescription?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const borderColor = useColorModeValue("gray.200", "gray.600");

  const clearError = (field: string) => {
    if (errors?.[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const getBorderStyles = (hasError: boolean) => ({
    borderColor: hasError ? "red.500" : borderColor,
  });

  const getFocusStyles = (hasError: boolean) => ({
    borderColor: hasError ? "red.500" : "#005EB8",
    boxShadow: hasError ? "0 0 0 1px #E53E3E" : "0 0 0 1px #005EB8",
  });

  const handleInputChange = (
    field: keyof AssessmentFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const addItem = (type: SelectorType, itemId: number) => {
    const selectedIds =
      type === "equipment" ? selectedEquipmentIds : selectedMaterialIds;
    const setSelectedIds =
      type === "equipment" ? setSelectedEquipmentIds : setSelectedMaterialIds;

    if (!selectedIds?.includes(itemId)) {
      setSelectedIds((prev) => [...(prev || []), itemId]);
    }
  };

  const removeItem = (type: SelectorType, itemId: number) => {
    const setSelectedIds =
      type === "equipment" ? setSelectedEquipmentIds : setSelectedMaterialIds;

    setSelectedIds((prev) => prev?.filter((id) => id !== itemId) || []);
  };

  const addEquipment = (equipmentId: number) =>
    addItem("equipment", equipmentId);

  const removeEquipment = (equipmentId: number) =>
    removeItem("equipment", equipmentId);

  const addMaterial = (materialId: number) => addItem("material", materialId);

  const removeMaterial = (materialId: number) =>
    removeItem("material", materialId);

  // Custom equipment functions
  const addCustomEquipment = (name: string) => {
    const trimmedName = name.trim();
    if (trimmedName && !customEquipments.includes(trimmedName)) {
      setCustomEquipments((prev) => [...prev, trimmedName]);
    }
    setEquipmentInputValue("");
    setEquipmentSearch("");
    setIsEquipmentMenuOpen(false);
  };

  const removeCustomEquipment = (name: string) => {
    setCustomEquipments((prev) => prev.filter((item) => item !== name));
  };

  // Custom material functions
  const addCustomMaterial = (name: string) => {
    const trimmedName = name.trim();
    if (trimmedName && !customMaterials.includes(trimmedName)) {
      setCustomMaterials((prev) => [...prev, trimmedName]);
    }
    setMaterialInputValue("");
    setMaterialSearch("");
    setIsMaterialMenuOpen(false);
  };

  const removeCustomMaterial = (name: string) => {
    setCustomMaterials((prev) => prev.filter((item) => item !== name));
  };

  // Handle equipment input change (client-side filtering only, no API refetch)
  const handleEquipmentInputChange = (value: string) => {
    setEquipmentInputValue(value);
    if (formData?.department?.id) {
      setIsEquipmentMenuOpen(true);
    }
  };

  // Handle equipment Enter key
  const handleEquipmentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && equipmentInputValue.trim()) {
      e.preventDefault();
      addCustomEquipment(equipmentInputValue);
    }
  };

  // Handle material input change (client-side filtering only, no API refetch)
  const handleMaterialInputChange = (value: string) => {
    setMaterialInputValue(value);
    if (formData?.department?.id) {
      setIsMaterialMenuOpen(true);
    }
  };

  // Handle material Enter key
  const handleMaterialKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && materialInputValue.trim()) {
      e.preventDefault();
      addCustomMaterial(materialInputValue);
    }
  };

  const selectDepartment = (department: any) => {
    setFormData((prev) => ({
      ...prev,
      department: department,
    }));
    setCustomDepartment("");
    setDepartmentSearch("");
    setIsDepartmentMenuOpen(false);
    clearError("department");
  };

  // Handle department input change
  const handleDepartmentInputChange = (value: string) => {
    setCustomDepartment(value);
    setDepartmentSearch(value);
    // Clear selected department when typing
    if (formData?.department) {
      setFormData((prev) => ({
        ...prev,
        department: null,
      }));
    }
    clearError("department");

    // Open menu when typing
    if (value.length > 0) {
      setIsDepartmentMenuOpen(true);
    }
  };

  // Handle department input click
  const handleDepartmentInputClick = () => {
    setIsDepartmentMenuOpen(true);
  };

  // Scroll handlers for infinite loading
  const handleDepartmentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    // Load more when scrolled to 80% of the list
    if (
      scrollPosition >= scrollHeight * 0.8 &&
      departmentHasMore &&
      !getDepartmentsLoading
    ) {
      fetchDepartments({
        searchQuery: departmentSearch,
        offset: departmentOffset,
        limit: ITEMS_PER_PAGE,
        append: true,
      });
      setDepartmentOffset((prev) => prev + ITEMS_PER_PAGE);
    }
  };

  const handleEquipmentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (
      scrollPosition >= scrollHeight * 0.8 &&
      equipmentHasMore &&
      !getEquipmentsByDepartmentLoading &&
      formData?.department?.id
    ) {
      fetchEquipmentsByDepartment({
        id: formData.department.id,
        searchQuery: equipmentSearch,
        offset: equipmentOffset,
        limit: ITEMS_PER_PAGE,
        append: true,
      });
      setEquipmentOffset((prev) => prev + ITEMS_PER_PAGE);
    }
  };

  const handleMaterialScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (
      scrollPosition >= scrollHeight * 0.8 &&
      materialHasMore &&
      !getMaterialsByDepartmentLoading &&
      formData?.department?.id
    ) {
      fetchMaterialsByDepartment({
        id: formData.department.id,
        searchQuery: materialSearch,
        offset: materialOffset,
        limit: ITEMS_PER_PAGE,
        append: true,
      });
      setMaterialOffset((prev) => prev + ITEMS_PER_PAGE);
    }
  };

  // Get display value for department input
  const getDepartmentDisplayValue = () => {
    if (formData?.department) {
      return formData.department.title;
    }
    return customDepartment;
  };

  // Filter departments based on search
  const filteredDepartments = departmentData?.filter((dept: any) =>
    dept?.title?.toLowerCase().includes(departmentSearch.toLowerCase())
  );

  // Check if menu should close (no matches and user typed something)
  useEffect(() => {
    if (
      customDepartment.length > 0 &&
      filteredDepartments?.length === 0 &&
      !getDepartmentsLoading
    ) {
      setIsDepartmentMenuOpen(false);
    }
  }, [filteredDepartments, customDepartment, getDepartmentsLoading]);

  const renderDepartmentSelector = () => {
    return (
      <Box position="relative" ref={departmentMenuRef}>
        <Box position="relative">
          <Input
            ref={departmentInputRef}
            placeholder={t("assessments.modal.departmentPlaceholder")}
            value={getDepartmentDisplayValue()}
            onChange={(e) => handleDepartmentInputChange(e.target.value)}
            onClick={handleDepartmentInputClick}
            {...getBorderStyles(!!errors?.department)}
            borderRadius="8px"
            pr="40px"
            _focus={getFocusStyles(!!errors?.department)}
            _placeholder={{
              color: "gray.500",
            }}
          />
          <Button
            position="absolute"
            right="0"
            top="0"
            h="100%"
            bg="transparent"
            minW="40px"
            p={0}
            _hover={{ bg: "transparent" }}
            _active={{ bg: "transparent" }}
            onClick={() => setIsDepartmentMenuOpen(!isDepartmentMenuOpen)}
          >
            <ChevronDownIcon
              transform={
                isDepartmentMenuOpen ? "rotate(180deg)" : "rotate(0deg)"
              }
              transition="transform 0.2s"
            />
          </Button>
        </Box>

        {/* Dropdown Menu */}
        {isDepartmentMenuOpen && (
          <Box
            position="absolute"
            top="100%"
            left="0"
            right="0"
            mt={1}
            maxH="300px"
            overflowY="auto"
            borderRadius="12px"
            border="1px solid #D6DFEB"
            boxShadow="lg"
            px={2}
            py={2}
            bg="white"
            zIndex={9999}
            onScroll={handleDepartmentScroll}
          >
            {filteredDepartments?.map((department: any) => (
              <Box
                key={department?.id}
                onClick={() => selectDepartment(department)}
                bg={
                  formData?.department?.id === department?.id
                    ? "#E6F3FF"
                    : "transparent"
                }
                color={
                  formData?.department?.id === department?.id
                    ? "#005EB8"
                    : "#1A202C"
                }
                borderRadius="6px"
                px={3}
                py={2}
                cursor="pointer"
                _hover={{
                  bg:
                    formData?.department?.id === department?.id
                      ? "#E6F3FF"
                      : "#F7F9FC",
                }}
              >
                <Text fontSize="14px">{department?.title}</Text>
              </Box>
            ))}
            {getDepartmentsLoading && (
              <Box textAlign="center" py={2}>
                <Text fontSize="12px" color="gray.500">
                  {t("assessments.modal.loadingMore")}
                </Text>
              </Box>
            )}
            {!getDepartmentsLoading &&
              filteredDepartments?.length === 0 &&
              customDepartment.length === 0 && (
                <Box textAlign="center" py={2}>
                  <Text fontSize="12px" color="gray.500">
                    {t("assessments.modal.noResults")}
                  </Text>
                </Box>
              )}
          </Box>
        )}
      </Box>
    );
  };

  const renderSelector = (type: SelectorType) => {
    const isDepartmentSelected = !!formData?.department?.id;
    const hasCustomDepartment = !!customDepartment?.trim();

    // Get data based on type
    const data =
      type === "equipment"
        ? equipmentsByDepartmentData
        : materialsByDepartmentData;
    const selectedIds =
      type === "equipment" ? selectedEquipmentIds : selectedMaterialIds;
    const customItems =
      type === "equipment" ? customEquipments : customMaterials;
    const inputValue =
      type === "equipment" ? equipmentInputValue : materialInputValue;
    const isMenuOpen =
      type === "equipment" ? isEquipmentMenuOpen : isMaterialMenuOpen;
    const setIsMenuOpen =
      type === "equipment" ? setIsEquipmentMenuOpen : setIsMaterialMenuOpen;
    const menuRef = type === "equipment" ? equipmentMenuRef : materialMenuRef;
    const handleInputChange =
      type === "equipment"
        ? handleEquipmentInputChange
        : handleMaterialInputChange;
    const handleKeyDown =
      type === "equipment" ? handleEquipmentKeyDown : handleMaterialKeyDown;
    const addItemFunc = type === "equipment" ? addEquipment : addMaterial;
    const removeItemFunc =
      type === "equipment" ? removeEquipment : removeMaterial;
    const removeCustomItemFunc =
      type === "equipment" ? removeCustomEquipment : removeCustomMaterial;
    const placeholderKey =
      type === "equipment"
        ? "assessments.modal.equipmentPlaceholder"
        : "assessments.modal.materialPlaceholder";

    // Filter data based on input value (client-side filtering)
    const filteredData = data?.filter((item: any) =>
      item?.title?.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Get selected items from data for chips
    const selectedItems = data?.filter((item: any) =>
      selectedIds?.includes(item?.id)
    );

    const totalSelected =
      (selectedIds?.length || 0) + (customItems?.length || 0);

    return (
      <Box ref={menuRef}>
        {/* Input Field */}
        <Box position="relative">
          <Input
            placeholder={t(placeholderKey)}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onClick={() => {
              if (isDepartmentSelected || hasCustomDepartment) {
                setIsMenuOpen(true);
              }
            }}
            onKeyDown={handleKeyDown}
            borderRadius="8px"
            pr="140px"
            _focus={getFocusStyles(false)}
            _placeholder={{
              color: "gray.500",
            }}
          />
          {/* Hint text and dropdown icon */}
          <Box
            position="absolute"
            right="0"
            top="0"
            h="100%"
            display="flex"
            alignItems="center"
            pr={2}
          >
            <Text fontSize="12px" color="gray.450" mr={2}>
              {t("assessments.modal.pressEnterToAdd")}
            </Text>
            {/* <Button
              bg="transparent"
              minW="24px"
              h="24px"
              p={0}
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              onClick={() => {
                if (isDepartmentSelected || hasCustomDepartment) {
                  setIsMenuOpen(!isMenuOpen);
                }
              }}
              isDisabled={!isDepartmentSelected && !hasCustomDepartment}
            >
              <ChevronDownIcon
                transform={isMenuOpen ? "rotate(180deg)" : "rotate(0deg)"}
                transition="transform 0.2s"
              />
            </Button> */}
          </Box>
        </Box>

        {/* Dropdown Menu - only show if department is selected */}
        {isMenuOpen && isDepartmentSelected && (
          <Box
            position="absolute"
            left="24px"
            right="24px"
            mt={1}
            maxH="300px"
            overflowY="auto"
            borderRadius="12px"
            border="1px solid #D6DFEB"
            boxShadow="lg"
            px={2}
            py={2}
            bg="white"
            zIndex={9999}
            onScroll={
              type === "equipment"
                ? handleEquipmentScroll
                : handleMaterialScroll
            }
          >
            {filteredData?.map((item: any) => {
              const isSelected = selectedIds?.includes(item?.id);
              return (
                <Box
                  key={item?.id}
                  onClick={() => {
                    if (isSelected) {
                      removeItemFunc(item?.id);
                    } else {
                      addItemFunc(item?.id);
                    }
                  }}
                  display="flex"
                  alignItems="center"
                  bg={isSelected ? "#E6F3FF" : "transparent"}
                  color={isSelected ? "#005EB8" : "#1A202C"}
                  borderRadius="6px"
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{
                    bg: isSelected ? "#E6F3FF" : "#F7F9FC",
                  }}
                >
                  <Checkbox
                    isChecked={isSelected}
                    colorScheme="blue"
                    mr={3}
                    pointerEvents="none"
                  />
                  <Text fontSize="14px">{item?.title}</Text>
                </Box>
              );
            })}
            {((type === "equipment" && getEquipmentsByDepartmentLoading) ||
              (type === "material" && getMaterialsByDepartmentLoading)) && (
              <Box textAlign="center" py={2}>
                <Text fontSize="12px" color="gray.500">
                  {t("assessments.modal.loadingMore")}
                </Text>
              </Box>
            )}
            {!getDepartmentsLoading &&
              filteredData?.length === 0 &&
              inputValue.length > 0 && (
                <Box textAlign="center" py={2}>
                  <Text fontSize="12px" color="gray.500">
                    {t("assessments.modal.noResultsPressEnter")}
                  </Text>
                </Box>
              )}
          </Box>
        )}

        {/* Selected Items as Chips */}
        {totalSelected > 0 && (
          <Wrap mt={3} spacing={2}>
            {/* Selected items from data */}
            {selectedItems?.map((item: any) => (
              <WrapItem key={`selected-${item?.id}`}>
                <Tag
                  size="md"
                  borderRadius="full"
                  variant="solid"
                  colorScheme="blue"
                  bg="#005EB8"
                  color="white"
                >
                  <TagLabel>{item?.title}</TagLabel>
                  <TagCloseButton onClick={() => removeItemFunc(item?.id)} />
                </Tag>
              </WrapItem>
            ))}
            {/* Custom items */}
            {customItems?.map((name: string) => (
              <WrapItem key={`custom-${name}`}>
                <Tag
                  size="md"
                  borderRadius="full"
                  variant="solid"
                  colorScheme="blue"
                  bg="#005EB8"
                  color="white"
                >
                  <TagLabel>{name}</TagLabel>
                  <TagCloseButton onClick={() => removeCustomItemFunc(name)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Box>
    );
  };

  const validateForm = (): boolean => {
    const newErrors: {
      assessmentName?: string;
      department?: string;
      workDescription?: string;
    } = {};

    if (!formData?.assessmentName?.trim()) {
      newErrors.assessmentName = t(
        "assessments.modal.validation.assessmentNameRequired"
      );
    }

    // Department is valid if either a department is selected OR a custom department is provided
    if (!formData?.department && !customDepartment?.trim()) {
      newErrors.department = t(
        "assessments.modal.validation.departmentRequired"
      );
    }

    if (!formData?.workDescription?.trim()) {
      newErrors.workDescription = t(
        "assessments.modal.validation.workDescriptionRequired"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const resetForm = () => {
    setFormData({
      assessmentName: "",
      department: null,
      equipment: "",
      materials: "",
      workDescription: "",
    });
    setSelectedEquipmentIds([]);
    setSelectedMaterialIds([]);
    setCustomDepartment("");
    setCustomEquipments([]);
    setCustomMaterials([]);
    setEquipmentInputValue("");
    setMaterialInputValue("");
    setDepartmentSearch("");
    setEquipmentSearch("");
    setMaterialSearch("");
    setIsDepartmentMenuOpen(false);
    setIsEquipmentMenuOpen(false);
    setIsMaterialMenuOpen(false);
    setErrors({});
    setIsSubmitting(false);
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      // Format data according to API requirements
      const submitData: any = {
        name: formData?.assessmentName,
        description: formData?.workDescription,
      };

      // Add department (either selected or custom)
      if (formData?.department?.id) {
        submitData.departmentId = formData.department.id;
      }
      if (customDepartment?.trim()) {
        submitData.customDepartment = customDepartment.trim();
      }

      // Add equipment IDs if any selected
      if (selectedEquipmentIds.length > 0) {
        submitData.equipmentIds = selectedEquipmentIds;
      }

      // Add custom equipments if any
      if (customEquipments.length > 0) {
        submitData.customEquipments = customEquipments;
      }

      // Add material IDs if any selected
      if (selectedMaterialIds.length > 0) {
        submitData.materialIds = selectedMaterialIds;
      }

      // Add custom materials if any
      if (customMaterials.length > 0) {
        submitData.customMaterials = customMaterials;
      }
      console.log("submitData", submitData);
      try {
        setIsSubmitting(true);
        const result = onSubmit(submitData as any);
        // Handle both async and sync onSubmit
        if (result instanceof Promise) {
          await result;
        }
        resetForm();
        onClose();
        // Call success callback after successful submission
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
      } catch (error) {
        console.error("Error submitting assessment:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <CustomPopup
      open={isOpen}
      onClose={handleClose}
      title={t("assessments.modal.title")}
      width="600px"
      bodyPadding="24px"
    >
      <VStack spacing={6} align="stretch">
        {/* Assessment Name */}
        <FormControl isRequired isInvalid={!!errors?.assessmentName}>
          <FormLabel fontSize="14px" fontWeight="600" color="#1A202C">
            {t("assessments.modal.assessmentName")}
          </FormLabel>
          <Input
            placeholder={t("assessments.modal.assessmentNamePlaceholder")}
            value={formData?.assessmentName || ""}
            onChange={(e) =>
              handleInputChange("assessmentName", e.target.value)
            }
            {...getBorderStyles(!!errors?.assessmentName)}
            borderRadius="8px"
            _focus={getFocusStyles(!!errors?.assessmentName)}
            _placeholder={{
              color: "gray.500",
            }}
          />
        </FormControl>

        {/* Department */}
        <FormControl isRequired isInvalid={!!errors?.department}>
          <FormLabel fontSize="14px" fontWeight="600" color="#1A202C">
            {t("assessments.modal.department")}
          </FormLabel>
          {renderDepartmentSelector()}
        </FormControl>

        {/* Equipment */}
        <FormControl>
          <FormLabel fontSize="14px" fontWeight="600" color="#1A202C">
            {t("assessments.modal.equipment")}
          </FormLabel>
          {renderSelector("equipment")}
        </FormControl>

        {/* Material */}
        <FormControl>
          <FormLabel fontSize="14px" fontWeight="600" color="#1A202C">
            {t("assessments.modal.material")}
          </FormLabel>
          {renderSelector("material")}
        </FormControl>

        {/* Work Description */}
        <FormControl isRequired isInvalid={!!errors?.workDescription}>
          <FormLabel fontSize="14px" fontWeight="600" color="#1A202C">
            {t("assessments.modal.workDescription")}
          </FormLabel>
          <Textarea
            placeholder={t("assessments.modal.workDescriptionPlaceholder")}
            value={formData?.workDescription || ""}
            onChange={(e) =>
              handleInputChange("workDescription", e.target.value)
            }
            {...getBorderStyles(!!errors?.workDescription)}
            borderRadius="8px"
            minH="100px"
            resize="vertical"
            _focus={getFocusStyles(!!errors?.workDescription)}
            _placeholder={{
              color: "gray.500",
            }}
          />
        </FormControl>

        {/* Submit Button */}
        <Button
          w="100%"
          bg="#005EB8"
          color="white"
          borderRadius="8px"
          py={4}
          fontSize="16px"
          fontWeight="500"
          onClick={handleSubmit}
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          loadingText={t("assessments.modal.createAssessment")}
          spinner={<Spinner size="sm" color="white" />}
          _hover={{
            bg: isSubmitting ? "#005EB8" : "#004A96",
          }}
          _active={{
            bg: isSubmitting ? "#005EB8" : "#003D7A",
          }}
          rightIcon={!isSubmitting ? <AddIcon boxSize={4} /> : undefined}
        >
          {t("assessments.modal.createAssessment")}
        </Button>
      </VStack>
    </CustomPopup>
  );
};

export default CreateAssessmentModal;
